import { NotePencilIcon } from "@phosphor-icons/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import type * as C from "@zooi/contracts";
import { useState } from "react";
import { api, q } from "./api.ts";
import { useRefresh } from "./components.tsx";
import { relative } from "./format.ts";
import { RULE_LABELS as L } from "./labels.ts";
import { Button, EmptyState, ErrorText, Field, Section } from "./ui.tsx";
import { ChannelChip } from "./ui/channel.tsx";
import { AgentText } from "./ui/review.tsx";
import { Skeleton } from "./ui/skeleton.tsx";

// A product's rules (docs/design-system.md §0.6, Rules tab): this product's own
// way of writing and working, kept apart from channel rules. What an agent
// proposed waits for the person, who keeps it, in their own words if they edit
// it, or declines it. The person can write one directly and retire any.

/** One proposal: the agent's rule and reason, and the person's call. */
export function RuleProposal({ rule, productId }: { rule: C.ProductRule; productId: string }) {
  const refresh = useRefresh(productId);
  const [text, setText] = useState(rule.text);
  const decide = useMutation({ mutationFn: (decision: "accept" | "decline") => api.post(`/api/product-rules/${rule.id}/decision`, decision === "accept" && text !== rule.text ? { decision, text } : { decision }), onSuccess: refresh });
  return <div className="z-rule">
    {rule.channel && <div><ChannelChip channel={rule.channel} /></div>}
    <Field label={L.rule}><textarea className="z-input" rows={2} maxLength={200} value={text} onChange={(event) => setText(event.target.value)} /></Field>
    {rule.why && <AgentText name="Agent" label={L.why} lines={4}>{rule.why}</AgentText>}
    <div className="z-inline">
      <Button size="sm" tone="primary" busy={decide.isPending && decide.variables === "accept"} disabled={!text.trim()} onClick={() => decide.mutate("accept")}>{L.keep}</Button>
      <Button size="sm" tone="ghost" busy={decide.isPending && decide.variables === "decline"} onClick={() => decide.mutate("decline")}>{L.decline}</Button>
    </div>
    <ErrorText error={decide.error} />
  </div>;
}

function RuleRow({ rule, productId }: { rule: C.ProductRule; productId: string }) {
  const refresh = useRefresh(productId);
  const retire = useMutation({ mutationFn: () => api.post(`/api/product-rules/${rule.id}/decision`, { decision: "retire" }), onSuccess: refresh });
  return <div className="z-lrow z-rule-row">
    <div className="z-lrow-text">
      <div className="z-lrow-title">{rule.text}</div>
      {rule.why && <div className="z-lrow-sub">{rule.why}</div>}
    </div>
    {rule.channel && <span className="z-lrow-chips"><ChannelChip channel={rule.channel} /></span>}
    <span className="z-lrow-meta">{rule.decided_at ? relative(rule.decided_at) : relative(rule.created_at)}</span>
    <Button size="sm" tone="ghost" busy={retire.isPending} onClick={() => retire.mutate()}>{L.retire}</Button>
    <ErrorText error={retire.error} />
  </div>;
}

function AddRule({ productId }: { productId: string }) {
  const refresh = useRefresh(productId);
  const [text, setText] = useState("");
  const [why, setWhy] = useState("");
  const add = useMutation({
    mutationFn: () => api.post(`/api/products/${encodeURIComponent(productId)}/rules`, { text, why: why.trim() || null, channel: null }),
    onSuccess: () => { setText(""); setWhy(""); return refresh(); },
  });
  return <form className="z-rule-add" onSubmit={(event) => { event.preventDefault(); add.mutate(); }}>
    <Field label={L.rule} help={L.ruleHint}><textarea className="z-input" rows={2} maxLength={200} value={text} onChange={(event) => setText(event.target.value)} /></Field>
    <Field label={L.whyOptional}><input className="z-input" maxLength={400} value={why} onChange={(event) => setWhy(event.target.value)} /></Field>
    <div className="z-inline"><Button size="sm" type="submit" busy={add.isPending} disabled={text.trim().length < 8}>{L.add}</Button></div>
    <ErrorText error={add.error} />
  </form>;
}

export function ProductRules({ productId }: { productId: string }) {
  const rules = useQuery(q.productRules(productId));
  if (rules.isLoading) return <Skeleton shape="list" rows={3} />;
  if (!rules.data) return <ErrorText error={rules.error} />;
  const waiting = rules.data.filter((rule) => rule.status === "proposed");
  const active = rules.data.filter((rule) => rule.status === "active");
  return <div className="z-flow">
    <p className="z-small z-muted">{L.note}</p>
    {waiting.length > 0 && <Section title={L.waiting} count={waiting.length}>
      <div className="z-flow">{waiting.map((rule) => <RuleProposal key={rule.id} rule={rule} productId={productId} />)}</div>
    </Section>}
    {active.length > 0
      ? <Section title={L.active} count={active.length}><div className="z-boxed">{active.map((rule) => <RuleRow key={rule.id} rule={rule} productId={productId} />)}</div></Section>
      : !waiting.length && <EmptyState icon={<NotePencilIcon size={24} />} title={L.emptyTitle}>{L.emptyNote}</EmptyState>}
    <Section title={L.write}><AddRule productId={productId} /></Section>
  </div>;
}
